import {
  useApplyCardsSettingsMutation,
  useGetCardsQuery,
  useResetCardIncomeMutation,
  useToggleAllCardsMutation,
} from '../../../../../../../redux/services/CardsApi';
import mapCards from '../../../../../../../utils/functions/map/mapCards';
import { Loading } from '../../../../../../../components/Loading/Loading';
import { type CardsModel } from '../../../../../../../utils/types';
import { Blocks } from '../../../../../../../components/Blocks/Blocks';
import { EditCardModal } from '../components/EditCardModal/EditCardModal';
import { type TCardsContentProps } from '../../CardsContent';
import React, { useEffect, useRef, useState } from 'react';
import { Pagination } from '../../../../../../../components/Pagination/Pagination';
import { pushNotify } from 'components/Notifications';
import { useFormContext } from 'react-hook-form';
import { parseStringToNumber } from '../../../../../../../utils/functions/parseNumber';

export const CardsBlocks = (props: TCardsContentProps) => {
  const { currentId, setCurrentId, page, setPage, pageSize, search, currentPageIds, setCurrentPageIds } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentCard, setCurrentCard] = useState<CardsModel>();
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [toggleAllCards, { isLoading: isToggleLoading }] = useToggleAllCardsMutation();
  const [resetCardIncome] = useResetCardIncomeMutation();
  const [applyCardsSettings, { isLoading: isApplyLoading }] = useApplyCardsSettingsMutation();
  const { getValues } = useFormContext();
  const { data, isLoading } = useGetCardsQuery(
    { page, page_size: pageSize, search },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const hasCards = Boolean(data?.total_count ?? data?.count ?? 0);
  const isActionsLoading = isToggleLoading || isApplyLoading;

  useEffect(() => {
    const ids = (data?.results || []).map((card) => card.id);
    setCurrentPageIds(ids);
  }, [data, setCurrentPageIds]);

  const handleTogglePage = async (active: boolean) => {
    if (currentPageIds.length === 0) {
      return;
    }
    await toggleAllCards({ active, ids: currentPageIds })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось изменить статус реквизитов', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleResetIncome = async (cardId: number) => {
    await resetCardIncome(cardId)
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
  };

  const handleApplySettingsAll = async () => {
    if (!currentId) {
      return;
    }

    const selectedCard = data?.results.find((card) => card.id === currentId);
    if (!selectedCard?.currency_code) {
      pushNotify('Не удалось определить валюту выбранного реквизита', 'Ошибка');
      return;
    }

    const values = getValues();
    await applyCardsSettings({
      currency_code: selectedCard.currency_code,
      total_limit: parseStringToNumber(values.newLimit),
      min_income: +values.newMinIncome,
      max_income: +values.newMaxIncome,
      instant_active_orders: parseStringToNumber(values.newInstantOrders),
      daily_total_limit: parseStringToNumber(values.newDailyLimit || '0'),
      monthly_total_limit: parseStringToNumber(values.newMonthlyLimit || '0'),
      daily_limit_enabled: !!values.newDailyLimitEnabled,
      monthly_limit_enabled: !!values.newMonthlyLimitEnabled,
    })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось применить настройки ко всем реквизитам', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!actionsMenuRef.current?.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  if (isLoading) {
    return <Loading />;
  }
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        <div style={{ padding: '8px 10px', border: '1px solid #e2e5f0', borderRadius: '6px' }}>
          Всего: {data?.total_count || 0}
        </div>
        <div style={{ padding: '8px 10px', border: '1px solid #e2e5f0', borderRadius: '6px' }}>
          Включено: {data?.active_count || 0}
        </div>
        <div style={{ padding: '8px 10px', border: '1px solid #e2e5f0', borderRadius: '6px' }}>
          Выключено: {data?.inactive_count || 0}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <div className='cards_actions' ref={actionsMenuRef}>
          <button
            type='button'
            className='cards_actions_button'
            disabled={isActionsLoading}
            onClick={() => setIsActionsOpen(!isActionsOpen)}
          >
            Действия
          </button>
          {isActionsOpen && (
            <div className='cards_actions_menu'>
              <div className='cards_actions_group'>Страница</div>
              <button
                type='button'
                className='cards_actions_item'
                disabled={!currentPageIds.length || isActionsLoading}
                onClick={() => handleTogglePage(true)}
              >
                Включить страницу
              </button>
              <button
                type='button'
                className='cards_actions_item'
                disabled={!currentPageIds.length || isActionsLoading}
                onClick={() => handleTogglePage(false)}
              >
                Выключить страницу
              </button>
              <div className='cards_actions_group'>Редактирование</div>
              <button
                type='button'
                className='cards_actions_item accent'
                disabled={!currentId || !hasCards || isActionsLoading}
                onClick={handleApplySettingsAll}
              >
                Применить настройки ко всем
              </button>
            </div>
          )}
        </div>
      </div>
      <Blocks
        blocks={
          mapCards(data?.results)?.map((card: CardsModel) => {
            return (
              <div key={card.id}>
                <div className={'block'}>
                  <div className={'component'}>
                    <p className={'block-component'}>Активность:</p>
                    <p className={'block-value'}>{card.active ? 'Активна' : 'Неактивна'}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Название реквизита:</p>
                    <p className={'block-value'}>{card.tag}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Описание:</p>
                    <p className={'block-value'}>{card.description}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Метод оплаты:</p>
                    <p className={'block-value'}>{card.payment_type}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Номер карты:</p>
                    <p className={'block-value'}>
                      {card.payment_type === 'C2C' ? card.account_number : <>–</>}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Номер счета:</p>
                    <p className={'block-value'}>
                      {card.payment_type === 'SBP' || card.payment_type === 'TRANSFER' ? (
                        card.account_number
                      ) : (
                        <>–</>
                      )}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>БИК</p>
                    <p className={'block-value'}>{card.bank.bic ?? '-'}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Банк:</p>
                    <p className={'block-value'}>{card.bank.name}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Назначение платежа</p>
                    <p className={'block-value'}>
                      {card.payment_type === 'TRANSFER' ? 'Перевод' : '-'}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Номер телефона:</p>
                    <p className={'block-value'}>{card.phone_number}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Общий лимит:</p>
                    <p className={'block-value'}>{card.total_limit}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Всего пополнений:</p>
                    <p className={'block-value'}>{card.total_income}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Дневной лимит:</p>
                    <p className={'block-value'}>
                      {card.daily_limit_enabled ? card.daily_total_limit : 'Выключен'}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Месячный лимит:</p>
                    <p className={'block-value'}>
                      {card.monthly_limit_enabled ? card.monthly_total_limit : 'Выключен'}
                    </p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Минимальная сумма платежа:</p>
                    <p className={'block-value'}>{card.min_income}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Максимальная сумма платежа:</p>
                    <p className={'block-value'}>{card.max_income}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>Заявок одновременно не более:</p>
                    <p className={'block-value'}>{card.instant_active_orders}</p>
                  </div>
                  <div className={'component'}>
                    <p className={'block-component'}>ФИО:</p>
                    <p className={'block-value'}>{card.fio}</p>
                  </div>
                  <div className={'block-buttons'}>
                    <div
                      className={'button-component'}
                      style={{ color: '#1890FF', marginTop: '6px', cursor: 'pointer' }}
                      onClick={() => {
                        setCurrentId(card.id);
                        setCurrentCard(card);
                        setModalOpen(true);
                      }}
                    >
                      <img src={'/edit_icon.png'} alt={'edit_icon'} />
                      Изменить
                    </div>
                    <div
                      className={'button-component'}
                      style={{ color: '#d97706', marginTop: '6px', cursor: 'pointer' }}
                      onClick={() => {
                        handleResetIncome(card.id);
                      }}
                    >
                      Обнулить пополнения
                    </div>
                  </div>
                </div>
              </div>
            );
          }) || []
        }
      />
      <EditCardModal open={modalOpen} setOpen={setModalOpen} card={currentCard} />
      <Pagination
        totalPages={Math.ceil((data?.count || 0) / pageSize)}
        setCurrentPage={setPage}
        currentPage={page}
        nextPage={typeof data?.next === 'number' ? data.next : page}
        previousPage={typeof data?.previous === 'number' ? data.previous : page}
      />
    </div>
  );
};
